import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { decamelizeKeys } from 'humps';
import type { IOpenTokSession } from '../views/flex/flexNodes/meetingRoomFlexNode/components/rtcProviders/opentok';
import useLanguage from '../hooks/useLanguage';
import useNavigation from '../hooks/useNavigation';
import useOverlay from '../hooks/useOverlay';
import { logError, logInfo, logWarning } from '../utils/remoteLogger';
import request from '../utils/request';
import { AppConfigContext } from './appConfigContext';
import { AppContext } from './appContext';
import useWsPushNotification from '../hooks/useWsPushNotification';
import RequestError from '../utils/requestError';
import useSnowplowTracker from '../hooks/useSnowplowTracker';
import device, { getPermissionBlockedDialogKeys } from '../utils/device';
import useRumTracker from '../hooks/useRumTracker';

export interface IMeetingInfo {
  meetingId?: string;
  callerTitle?: string;
  callerSubtitle?: string;
  callingTitle?: string;
  videoTitle?: string;
  videoSubtitle?: string;
  waitingRoomTitle?: string;
  waitingRoomSubtitle?: string;
  careType?: ICareType;
  ringtone?: unknown;
  symptomId?: string;
  type?: unknown;
  userId?: unknown;

  doctor?: {
    id?: string;
    name?: string;
    doctorImage?: string;
    title?: string;
    careType?: unknown;
    facility?: unknown;
    facilityLocation?: unknown;
    poolId?: unknown;
    profession?: unknown;
    questions?: Array<unknown>;
  };
}

export enum ICareType {
  PRIMARY = 1,
  REGULAR_CARE = 1,
  NURSE = 2,
  PSYCHOLOGY = 3,
}

export enum IMeetingTimelineEvent {
  VIDEO_PATIENT_CALL_PUSH_RECEIVED = 'VIDEO_PATIENT_CALL_PUSH_RECEIVED',
  VIDEO_PATIENT_CALL_SCREEN_PRESENTED = 'VIDEO_PATIENT_CALL_SCREEN_PRESENTED',
  VIDEO_PATIENT_CALL_ANSWERED = 'VIDEO_PATIENT_CALL_ANSWERED',
  VIDEO_PATIENT_CONNECTING = 'VIDEO_PATIENT_CONNECTING',
  VIDEO_PATIENT_CALL_DECLINED = 'VIDEO_PATIENT_CALL_DECLINED',
  VIDEO_PATIENT_CALL_HUNG_UP = 'VIDEO_PATIENT_CALL_HUNG_UP',
  VIDEO_PATIENT_SWITCH_CAMERA = 'VIDEO_PATIENT_SWITCH_CAMERA',
  VIDEO_PATIENT_WAITING_ROOM_JOINED = 'VIDEO_PATIENT_WAITING_ROOM_JOINED',
  VIDEO_PATIENT_WAITING_ROOM_LEFT = 'VIDEO_PATIENT_WAITING_ROOM_LEFT',
  VIDEO_PATIENT_REMOTE_STREAM_CONNECTED = 'VIDEO_PATIENT_REMOTE_STREAM_CONNECTED',
  VIDEO_PATIENT_MIC_MUTED = 'VIDEO_PATIENT_MIC_MUTED',
  VIDEO_PATIENT_MIC_UNMUTED = 'VIDEO_PATIENT_MIC_UNMUTED',
  VIDEO_PATIENT_CAMERA_DISABLED = 'VIDEO_PATIENT_CAMERA_DISABLED',
  VIDEO_PATIENT_CAMERA_ENABLED = 'VIDEO_PATIENT_CAMERA_ENABLED',
  CLIENT_AUTH_ERROR = 'CLIENT_AUTH_ERROR',
  CLIENT_CONNECTION_ERROR = 'CLIENT_CONNECTION_ERROR',
  CLIENT_LIFECYCLE_ERROR = 'CLIENT_LIFECYCLE_ERROR',
  // Not tracked on web: the calling screen is presented as an overlay with no notification permission gate
  CLIENT_CALL_SCREEN_NOT_PRESENTED = 'CLIENT_CALL_SCREEN_NOT_PRESENTED',
  // Not tracked on web: we can't call the timeline event api when not logged in on web, the web client doesn't have any private/public keys to sign the requests
  PATIENT_SIGNS_IN = 'PATIENT_SIGNS_IN',
  PATIENT_SIGN_IN_SUCCEEDED = 'PATIENT_SIGN_IN_SUCCEEDED',
  PATIENT_SIGN_IN_FAILED = 'PATIENT_SIGN_IN_FAILED',
  VIDEO_PATIENT_PERMISSIONS_GRANTED = 'VIDEO_PATIENT_PERMISSIONS_GRANTED',
  VIDEO_PATIENT_PERMISSIONS_DENIED = 'VIDEO_PATIENT_PERMISSIONS_DENIED',
  VIDEO_PATIENT_MEDIA_INTERRUPTED = 'VIDEO_PATIENT_MEDIA_INTERRUPTED',
  VIDEO_PATIENT_MEDIA_RESUMED = 'VIDEO_PATIENT_MEDIA_RESUMED',
}

export interface IMeetingSession {
  opentokSession?: IOpenTokSession;
}

// Tags the OpenTok-error alerts presented by `showStreamError` /
// `showPermissionError`. Lets `MeetingRoomFlexNode` positively identify
// the alert on reconnect so it can be auto-dismissed without affecting
// unrelated overlays that may be on screen.
export const STREAM_ERROR_ALERT_ID = 'stream-error';

interface IUpcomingMeeting {
  needsVideoAudioPermission: boolean;
  shouldDisableIdleTimer: boolean;
}

interface IMeetingContext {
  meetingInfo?: IMeetingInfo;
  meetingSession?: IMeetingSession;
  callAccepted?: boolean;
  upcomingMeeting?: IUpcomingMeeting;
  setMeetingRoomId: (meetingId?: string) => void;
  reportOngoingCall: (meetingId: string) => void;
  getMeetingInfo: (meetingId: string) => Promise<IMeetingInfo>;
  setMeetingSession: React.Dispatch<
    React.SetStateAction<IMeetingSession | undefined>
  >;
  endCall: (meetingId: string) => void;
  trackTimelineEvent: (
    meetingId: string,
    eventType: IMeetingTimelineEvent,
    eventDetails?: string
  ) => void;
  reportConnection: (sessionId: string, connectionId?: string) => void;

  showEndCallConfirmation: (meetingId: string, handler: () => void) => void;
  showMeetingEndedConfirmation: (handler: () => void) => void;
  showSessionError: (handler: () => void) => void;
  showStreamError: (handler: () => void) => void;
  showPermissionError: (handler: () => void) => void;
  showMeetingPausedPrompt: (onResume: () => void) => void;
  resetMeetingSession: (reason: string) => void;
  pollMeetingsNow: () => void;
  setBlockedByOtherTab: (blocked: boolean, resetSession?: boolean) => void;
}

export const MeetingContext = React.createContext({} as IMeetingContext);

interface IMeetingContextProvider {
  children: ReactNode;
}

export const MeetingContextProvider = React.memo(
  ({ children }: IMeetingContextProvider) => {
    const [upcomingMeeting, setUpcomingMeeting] = useState<IUpcomingMeeting>();
    const [meetingSession, setMeetingSession] = useState<IMeetingSession>();
    const [meetingInfo, setMeetingInfo] = useState<IMeetingInfo>();
    const [callAccepted, setCallAccepted] = useState<boolean>();
    const [meetingRoomId, setMeetingRoomIdState] = useState<string>();
    const [isBlockedByOtherTab, setIsBlockedByOtherTab] = useState(false);

    const { isUserLoggedIn, appConfig } = useContext(AppConfigContext);
    const { enableIdleDetection } = useContext(AppContext);

    const navigation = useNavigation();
    const language = useLanguage();
    const overlay = useOverlay();
    const snowplowTracker = useSnowplowTracker();
    const rumTracker = useRumTracker();

    const missedCallNotification = useWsPushNotification('missed_call');
    const startVideoMeetingNotification = useWsPushNotification(
      'start_video_meeting'
    );

    const pollOngoingMeetingTimeoutRef = useRef<number>();
    const pollUpcomingMeetingTimeoutRef = useRef<number>();
    const meetingRoomIdRef = useRef<string>();

    // Used to keep track of the current meeting info for the VIDEO_PATIENT_CALL_SCREEN_PRESENTED event
    const prevMeetingInfoRef = useRef<IMeetingInfo>();

    // Store the latest callAccepted value so updateMeetingInfo can use it
    // without being recreated when callAccepted changes.
    const callAcceptedRef = useRef(callAccepted);
    callAcceptedRef.current = callAccepted;

    // Updates meetingInfo while keeping the same object reference once the call
    // is accepted and in progress. The poller fetches a new meetingInfo object on
    // every refresh, and replacing it during a call would re-run effects that
    // depend on meetingInfo, potentially causing duplicate OpenTok sessions.
    //
    // Before the call is accepted, we still use the latest meetingInfo so updates
    // such as the assigned doctor's name or title are shown on the calling screen.
    const updateMeetingInfo = useCallback((meetingInfoArg: IMeetingInfo) => {
      setMeetingInfo((prev) =>
        callAcceptedRef.current &&
        prev &&
        prev.meetingId === meetingInfoArg.meetingId
          ? prev
          : meetingInfoArg
      );
    }, []);

    // Resets states, which in turn resumes polling.
    const resetMeetingSession = useCallback(
      (reason: string) => {
        logInfo(
          'MeetingContext',
          `Resetting meeting session and related states. Reason: ${reason}`
        );
        setMeetingInfo(undefined);
        setMeetingSession(undefined);
        setCallAccepted(false);
        prevMeetingInfoRef.current = undefined;
        rumTracker.clearMeetingId();
      },
      [rumTracker]
    );

    const setBlockedByOtherTab = useCallback(
      (blocked: boolean, resetSession = true) => {
        setIsBlockedByOtherTab(blocked);
        if (blocked && resetSession) {
          resetMeetingSession('blocked by another tab');
        }
      },
      [resetMeetingSession]
    );

    // Called when entering or existing the meetingRoom flex node.
    // Used to track whether we already are in the correct meeting room nor not.
    const setMeetingRoomId = useCallback((meetingId?: string) => {
      meetingRoomIdRef.current = meetingId;
      setMeetingRoomIdState(meetingId);
    }, []);

    const showSessionError = useCallback(
      (handler: () => void) => {
        logInfo('MeetingContext', 'Showing session error alert');
        overlay.presentAlert({
          title: language.get('stream_error_title'),
          message: language.get('stream_error_msg'),
          items: [
            {
              title: language.get('ok'),
              onClick: () => {
                handler();
              },
            },
          ],
        });
      },
      [overlay, language]
    );

    const showStreamError = useCallback(
      (handler: () => void) => {
        logInfo('MeetingContext', 'Showing stream error alert');
        overlay.presentAlert({
          id: STREAM_ERROR_ALERT_ID,
          title: language.get('stream_error_title'),
          message: language.get('stream_error_msg'),
          items: [
            {
              title: language.get('ok'),
              onClick: () => {
                handler();
              },
            },
          ],
        });
      },
      [overlay, language]
    );

    const showPermissionError = useCallback(
      (handler: () => void) => {
        const { titleKey, messageKey, buttonKey } =
          getPermissionBlockedDialogKeys('ok');
        logInfo('MeetingContext', `Showing permission error alert`, {
          variant: device.PERMISSION_DIALOG_VARIANT,
        });

        overlay.presentAlert({
          id: STREAM_ERROR_ALERT_ID,
          title: language.get(titleKey),
          message: language.get(messageKey),
          items: [
            {
              title: language.get(buttonKey),
              onClick: () => {
                handler();
              },
            },
          ],
        });
      },
      [overlay, language]
    );

    const showEndCallConfirmation = useCallback(
      (meetingId: string, handler: () => void) => {
        const endingCurrentCall = meetingInfo?.meetingId === meetingId;
        if (!endingCurrentCall) {
          snowplowTracker.trackEvent({
            ...snowplowTracker.event.MEETING_ROOM_PRE_CALL_LEAVING_ATTEMPT,
            label: meetingId,
          });
        }
        overlay.presentAlert({
          title: language.get(
            endingCurrentCall
              ? 'meeting_room_hangup_confirmation_title'
              : 'meeting_room_leave_waiting_room_confirmation_title'
          ),
          message: language.get(
            endingCurrentCall
              ? 'meeting_room_hangup_confirmation_message'
              : 'meeting_room_leave_waiting_room_confirmation_message'
          ),
          items: [
            {
              title: language.get(
                endingCurrentCall
                  ? 'meeting_room_hangup_confirmation_cancel'
                  : 'meeting_room_leave_waiting_room_confirmation_cancel'
              ),
              onClick: () => {
                // Do nothing
              },
            },
            {
              title: language.get(
                endingCurrentCall
                  ? 'meeting_room_hangup_confirmation_confirm'
                  : 'meeting_room_leave_waiting_room_confirmation_confirm'
              ),
              onClick: () => {
                handler();
                if (!endingCurrentCall) {
                  snowplowTracker.trackEvent({
                    ...snowplowTracker.event.MEETING_ROOM_PRE_CALL_ROOM_LEFT,
                    label: meetingId,
                  });
                }
              },
            },
          ],
        });
      },
      [overlay, language, meetingInfo, snowplowTracker]
    );

    const showMeetingEndedConfirmation = useCallback(
      (handler: () => void) => {
        overlay.presentAlert({
          title: language.get('meeting_ended_title'),
          message: language.get('meeting_ended_info'),
          items: [
            {
              title: language.get('ok'),
              onClick: () => {
                handler();
              },
            },
          ],
        });
      },
      [overlay, language]
    );

    const showMeetingPausedPrompt = useCallback(
      (onResume: () => void) => {
        logInfo('MeetingContext', 'Showing meeting paused prompt');
        overlay.presentAlert({
          title: language.get('patient_web.meeting_interrupted.title'),
          message: language.get('patient_web.meeting_interrupted.message'),
          items: [
            {
              title: language.get(
                'patient_web.meeting_interrupted.resume_button'
              ),
              onClick: () => {
                onResume();
              },
            },
          ],
        });
      },
      [overlay, language]
    );

    const trackTimelineEvent = useCallback(
      (
        meetingId: string,
        event: IMeetingTimelineEvent,
        eventDetails?: string
      ) => {
        if (meetingId) {
          request(appConfig.meetingVideoEndpoints.trackTimelineEvent, {
            method: 'POST',
            body: decamelizeKeys({
              meetingId,
              timestamp: Date.now(),
              event,
              type: 'VIDEO',
              userType: 'PATIENT',
              source: 'CLIENT',
              ...(eventDetails && { eventDetails }),
            }),
            silent: true,
            retryable: true,
            persistent: true,
          }).catch((error: RequestError) => {
            logError(
              'MeetingContext',
              `Failed posting timeline event: ${event}`,
              error
            );
          });
        }
      },
      [appConfig]
    );

    // Reporting connection ID for potential kry-meeting force disconnection from clinician side
    const reportConnection = useCallback(
      (sessionId: string, connectionId?: string) => {
        if (connectionId) {
          request(appConfig.meetingVideoEndpoints.reportConnection, {
            method: 'PUT',
            body: decamelizeKeys({
              sessionId,
              connectionId,
            }),
            silent: true,
            persistent: true,
          }).catch((error: RequestError) => {
            logError(
              'MeetingContext',
              `Failed posting connection report`,
              error
            );
          });
        }
      },
      [appConfig]
    );

    const declineCall = useCallback(
      (meetingId: string) => {
        trackTimelineEvent(
          meetingId,
          IMeetingTimelineEvent.VIDEO_PATIENT_CALL_DECLINED
        );
        request(appConfig.meetingVideoEndpoints.decline, {
          method: 'POST',
          body: {
            meeting_id: meetingId,
            timestamp: Date.now(),
            signature: '',
          },
        })
          .then(() => {
            resetMeetingSession('call declined by user');
          })
          .catch((error: RequestError) => {
            resetMeetingSession('decline call request failed');
            logError('MeetingContext', `Failed declining the call`, error);
          });
      },
      [appConfig, resetMeetingSession, trackTimelineEvent]
    );

    const acceptCall = useCallback(
      (meetingInfoArg: IMeetingInfo) => {
        if (meetingInfoArg.meetingId) {
          setCallAccepted(true);
          rumTracker.setMeetingId(meetingInfoArg.meetingId);

          trackTimelineEvent(
            meetingInfoArg.meetingId,
            IMeetingTimelineEvent.VIDEO_PATIENT_CALL_ANSWERED
          );

          // If we are not already are in the meeting room flex flow for this particular meeting,
          // start the flex flow.
          if (meetingRoomIdRef.current !== meetingInfoArg.meetingId) {
            navigation.push({
              type: 'FLEX',
              source: {
                call: {
                  url: `${appConfig.meetingRoomUrl}?meeting_id=${meetingInfoArg.meetingId}&is_video_call_ongoing=true`,
                  method: 'POST',
                },
              },
            });
          }
        }
      },
      [appConfig.meetingRoomUrl, navigation, rumTracker, trackTimelineEvent]
    );

    const getMeetingInfo = useCallback((meetingId: string) => {
      return request<IMeetingInfo>(`/api/view/meeting/booked/${meetingId}`, {
        silent: true,
        persistent: true,
      });
    }, []);

    const endCall = useCallback(
      (meetingId: string) => {
        trackTimelineEvent(
          meetingId,
          IMeetingTimelineEvent.VIDEO_PATIENT_CALL_HUNG_UP
        );
        request(appConfig.meetingVideoEndpoints.hangup, {
          method: 'POST',
          body: {
            meeting_id: meetingId,
          },
        }).catch((error: RequestError) => {
          resetMeetingSession('hangup request failed');
          logError('MeetingContext', `Failed ending the call`, error);
        });
      },
      [
        appConfig.meetingVideoEndpoints.hangup,
        resetMeetingSession,
        trackTimelineEvent,
      ]
    );

    const showCallingScreen = useCallback(
      (meetingInfoArg: IMeetingInfo) => {
        // Making sure that we dont track duplicate events or present the overlay
        // screen multiple times every time the poller runs
        if (
          prevMeetingInfoRef.current?.meetingId !== meetingInfoArg.meetingId &&
          meetingInfoArg.meetingId
        ) {
          if (isBlockedByOtherTab) {
            prevMeetingInfoRef.current = meetingInfoArg;
            return;
          }

          logInfo('MeetingContext', 'Showing calling screen');

          setCallAccepted(false);

          // Dismiss a potential pre-existing overlay before presenting the new one.
          overlay.dismiss();

          trackTimelineEvent(
            meetingInfoArg.meetingId,
            IMeetingTimelineEvent.VIDEO_PATIENT_CALL_SCREEN_PRESENTED
          );

          overlay.presentCallingScreen({
            meetingInfo: meetingInfoArg,
            persistent: true,

            onCallAccepted: () => {
              if (meetingInfoArg.meetingId) {
                acceptCall(meetingInfoArg);
              }
            },
            onCallDeclined: () => {
              if (meetingInfoArg.meetingId) {
                declineCall(meetingInfoArg.meetingId);
              }
            },
          });

          prevMeetingInfoRef.current = meetingInfoArg;
        }
      },
      [
        acceptCall,
        declineCall,
        isBlockedByOtherTab,
        overlay,
        trackTimelineEvent,
      ]
    );

    useEffect(() => {
      const handleMeetingInfoChange = () => {
        //  Issue: Ringing tone is heard even though the calling screen is not shown this is because it takes few ms to rerun the effect after broadcast message
        //  This makes the dismissal immediate rather than waiting for meetingInfo to be cleared through the chain
        //   resetMeetingSession → setMeetingInfo(undefined) → effect re-run.
        if (
          isBlockedByOtherTab &&
          overlay.getCurrentOverlayType() === 'CALLING_SCREEN'
        ) {
          overlay.dismiss();
          return;
        }
        if (callAccepted) {
          // If the call is already accepted, we don't want to trigger any of the below logic, which is meant for incoming calls.
          return;
        }

        // Use both the ref (sync, normal path) and the state (handles the race where
        // meetingInfo arrives before setMeetingRoomId runs on mount).
        const alreadyInMeetingRoom =
          !!meetingInfo?.meetingId &&
          (meetingRoomIdRef.current === meetingInfo.meetingId ||
            meetingRoomId === meetingInfo.meetingId);

        if (!isBlockedByOtherTab && alreadyInMeetingRoom) {
          // Auto-accept the call without showing CallingScreen
          logInfo(
            'MeetingContext',
            'Auto-accepting the call and skipping the calling screen'
          );
          acceptCall(meetingInfo);
          return;
        }

        if (
          !isBlockedByOtherTab &&
          meetingInfo?.meetingId &&
          overlay.getCurrentOverlayType() !== 'CALLING_SCREEN'
        ) {
          // Trigger the calling screen if not already shown.
          showCallingScreen(meetingInfo);
        } else if (
          !meetingInfo &&
          overlay.getCurrentOverlayType() === 'CALLING_SCREEN'
        ) {
          // If the clinician ends the call before the user accepts,
          // dismiss the calling screen.
          logInfo(
            'MeetingContext',
            'Dismissing calling screen as there is no meeting info'
          );
          overlay.dismiss();
        }
      };

      handleMeetingInfoChange();
    }, [
      meetingInfo,
      meetingRoomId,
      showCallingScreen,
      overlay,
      acceptCall,
      callAccepted,
      isBlockedByOtherTab,
    ]);

    // Enable idle detection if there is no upcoming meeting that requires it, no meeting info (which means no incoming call), and we're not already in a meeting room.
    useEffect(() => {
      const isInCallOrWaiting =
        meetingRoomId !== undefined || meetingInfo !== undefined;
      const enableIdle =
        upcomingMeeting?.shouldDisableIdleTimer !== true && !isInCallOrWaiting;
      enableIdleDetection(enableIdle);
    }, [
      meetingInfo,
      meetingRoomId,
      upcomingMeeting?.shouldDisableIdleTimer,
      enableIdleDetection,
    ]);

    // Get upcoming meetings.
    // This doesn't actually return an a list of upcoming meetings,
    // but rather instructs the client to e.g. disable the idle detection.
    const getUpcomingMeeting = useCallback(() => {
      return request<IUpcomingMeeting>(
        appConfig.meetingVideoEndpoints.getUpcoming,
        {
          silent: true,
          persistent: true,
        }
      )
        .then((upcoming) => {
          setUpcomingMeeting(upcoming);
        })
        .catch((error: RequestError) => {
          logWarning(
            'MeetingContext',
            `Failed getting upcoming meetings`,
            error
          );
        });
    }, [appConfig.meetingVideoEndpoints.getUpcoming]);

    // Get a potential ongoing meeting.
    const getOngoingMeeting = useCallback(() => {
      return request<{ meetingId?: string }>(
        appConfig.meetingVideoEndpoints.getOnGoing,
        {
          silent: true,
          persistent: true,
        }
      )
        .then((ongoingMeeting) => {
          if (ongoingMeeting.meetingId) {
            return getMeetingInfo(ongoingMeeting.meetingId).then(
              (meetingInfoArg) => {
                updateMeetingInfo(meetingInfoArg);

                if (!meetingInfoArg.meetingId) {
                  logError(
                    'MeetingContext',
                    `Got unexpected meeting info data`
                  );
                }
              }
            );
          }
          if (
            overlay.getCurrentOverlayType() === 'CALLING_SCREEN' &&
            !callAcceptedRef.current
          ) {
            // The clinician stopped the call before the user accepted, so we should dismiss the calling screen.
            resetMeetingSession(
              `ongoing-meeting poll returned no meeting while calling screen shown and call not accepted (callAccepted=${callAcceptedRef.current})`
            );
          }
        })
        .catch((error: RequestError) => {
          // Report error and fail silently. The request will be retried on the next poll.
          logWarning('MeetingContext', `Failed polling ongoing meeting`, error);
        });
    }, [
      appConfig.meetingVideoEndpoints.getOnGoing,
      getMeetingInfo,
      updateMeetingInfo,
      overlay,
      resetMeetingSession,
    ]);

    const pollMeetingsNow = useCallback(() => {
      if (pollOngoingMeetingTimeoutRef.current) {
        getOngoingMeeting().catch(() => {}); // Prevent unhandled rejection in fire-and-forget context
      }
      if (pollUpcomingMeetingTimeoutRef.current) {
        getUpcomingMeeting().catch(() => {}); // Prevent unhandled rejection in fire-and-forget context
      }
    }, [getOngoingMeeting, getUpcomingMeeting]);

    // Poll for upcoming and ongoing meetings.
    useEffect(() => {
      // Start polling for upcoming and ongoing meetings as soon as the user logs in,
      // pause if a call is accepted or the meeting is taking place, and stop if the user logs out.
      // Repetitively check for upcoming and ongoing meetings.
      // The polling continues up until we receive an actual meeting/video session.
      if (
        isUserLoggedIn &&
        !meetingSession &&
        !callAccepted &&
        appConfig.meetingVideoEndpoints
      ) {
        setTimeout(() => {
          // Fetch once in case there is already call screen to show,
          // without waiting for the ongoingMeetingPollInterval or upcomingMeetingPollInterval
          getOngoingMeeting().catch(() => {}); // Prevent unhandled rejection in fire-and-forget context
          getUpcomingMeeting().catch(() => {}); // Prevent unhandled rejection in fire-and-forget context
        }, 1000);
        // Start polling after potentially stopping previous interval.
        clearInterval(pollOngoingMeetingTimeoutRef.current);
        clearInterval(pollUpcomingMeetingTimeoutRef.current);

        logInfo('MeetingContext', 'Starting ongoing meeting poll intervals');
        pollOngoingMeetingTimeoutRef.current = window.setInterval(() => {
          getOngoingMeeting().catch(() => {}); // Prevent unhandled rejection in fire-and-forget context
        }, (appConfig.ongoingMeetingPollInterval || 15) * 1000);

        pollUpcomingMeetingTimeoutRef.current = window.setInterval(() => {
          getUpcomingMeeting().catch(() => {}); // Prevent unhandled rejection in fire-and-forget context
        }, (appConfig.upcomingMeetingPollInterval || 60) * 1000);
      } else {
        if (pollOngoingMeetingTimeoutRef.current) {
          logInfo('MeetingContext', 'Stopping ongoing meeting poll intervals');
        }
        clearInterval(pollOngoingMeetingTimeoutRef.current);
        pollOngoingMeetingTimeoutRef.current = undefined;
        clearInterval(pollUpcomingMeetingTimeoutRef.current);
        pollUpcomingMeetingTimeoutRef.current = undefined;
      }

      return () => {
        if (pollOngoingMeetingTimeoutRef.current) {
          logInfo('MeetingContext', 'Stopping ongoing meeting poll intervals');
        }
        clearInterval(pollOngoingMeetingTimeoutRef.current);
        pollOngoingMeetingTimeoutRef.current = undefined;
        clearInterval(pollUpcomingMeetingTimeoutRef.current);
        pollUpcomingMeetingTimeoutRef.current = undefined;
      };
    }, [
      appConfig,
      meetingSession,
      callAccepted,
      isUserLoggedIn,
      getOngoingMeeting,
      getUpcomingMeeting,
    ]);

    // Handle the "start-video-meeting" push notification.
    // Its payload contains a meetingId which we'll use to fetch the meeting details.
    // Once the details have been set to state, the calling screen will be presented.
    useEffect(() => {
      if (startVideoMeetingNotification) {
        trackTimelineEvent(
          startVideoMeetingNotification.payload.meetingId,
          IMeetingTimelineEvent.VIDEO_PATIENT_CALL_PUSH_RECEIVED
        );
        getMeetingInfo(startVideoMeetingNotification.payload.meetingId)
          .then((meetingInfoArg) => {
            updateMeetingInfo(meetingInfoArg);
          })
          .catch((error: RequestError) => {
            // Report error and fail silently. The request will be retried on the next poll.
            logError(
              'MeetingContext',
              `Failed getting meeting info after receiving the VOIP push`,
              error
            );
          });
      }
    }, [
      startVideoMeetingNotification,
      getMeetingInfo,
      updateMeetingInfo,
      trackTimelineEvent,
    ]);

    // Ongoing call reported when the meeting room node was mounted
    const reportOngoingCall = useCallback(
      (meetingId: string) => {
        getMeetingInfo(meetingId)
          .then((meetingInfoArg) => {
            updateMeetingInfo(meetingInfoArg);
          })
          .catch((error: RequestError) => {
            // Report error and fail silently. The request will be retried on the next poll.
            logError(
              'MeetingContext',
              `Failed getting meeting info after receiving meeting room mounted event for an ongoing call`,
              error
            );
          });
      },
      [getMeetingInfo, updateMeetingInfo]
    );

    // Handle the "missed-call" push notification.
    // It's received if the clinician cancels before the user picks up,
    // as well as when the user joins the call, in order to dismiss any ringing on other devices.
    // Clearing the meetingInfo state will also dismiss the calling screen.
    //
    // Guard against the join-time broadcast tearing down a live call: a
    // missed_call is also sent to THIS device when the user joins (to stop
    // ringing on their other devices). Only reset if the call is not already in
    // progress on this device. We read the live `callAcceptedRef.current` (the
    // `callAccepted` state can lag behind acceptCall()) and treat an active
    // `meetingSession` as in-progress, so a stop-ring can't drop a connected call.
    useEffect(() => {
      const callInProgress = callAcceptedRef.current || !!meetingSession;
      if (missedCallNotification && !callInProgress) {
        resetMeetingSession(
          `missed-call notification received while call not in progress (callAccepted=${
            callAcceptedRef.current
          }, hasSession=${!!meetingSession})`
        );
      }
    }, [missedCallNotification, meetingSession, resetMeetingSession]);

    return (
      <MeetingContext.Provider
        value={{
          meetingInfo,
          meetingSession,
          callAccepted,
          upcomingMeeting,
          setMeetingRoomId,
          reportOngoingCall,
          getMeetingInfo,
          setMeetingSession,
          endCall,

          showSessionError,
          showStreamError,
          showPermissionError,
          showMeetingPausedPrompt,
          showEndCallConfirmation,
          showMeetingEndedConfirmation,
          trackTimelineEvent,
          reportConnection,
          resetMeetingSession,
          pollMeetingsNow,
          setBlockedByOtherTab,
        }}
      >
        {children}
      </MeetingContext.Provider>
    );
  }
);
